import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TrendingUp, X } from "lucide-react";
import { api, shopToday } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

/** First sign-in of a Melbourne day: whoever sets the rates is asked to set them. */
export const RatePrompt = () => {
  const { user, branch, can } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);

  useEffect(() => {
    if (!user || !can("rates:write") || !branch) return;
    const key = `dj_rate_prompt_${branch}_${shopToday()}`;
    if (localStorage.getItem(key)) return;
    api
      .get("/gold-rates/status", { params: { branch } })
      .then(({ data }) => {
        // Nag until every purity is set — a half-set day still prices invoices wrongly.
        if (data.missing?.length) setStatus(data);
      })
      .catch(() => {});
  }, [user, branch, can]);

  if (!status) return null;

  const dismiss = () => {
    localStorage.setItem(`dj_rate_prompt_${branch}_${shopToday()}`, "1");
    setStatus(null);
  };

  return (
    <div
      className="no-print fixed inset-x-0 top-4 z-[60] flex justify-center px-4 pointer-events-none"
      data-testid="rate-prompt"
    >
      <div className="pointer-events-auto w-full max-w-md border border-[#C5A059] bg-white p-6 shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[#C5A059]" />
            <h2 className="text-base font-bold md:text-lg">
              {status.set_today
                ? "Today's rates are only part set"
                : "Today's gold rate is not set"}
            </h2>
          </div>
          <button
            type="button"
            onClick={dismiss}
            className="text-muted-foreground hover:text-foreground"
            data-testid="rate-prompt-dismiss"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-3 text-sm text-muted-foreground">
          {status.set_today ? (
            <>
              Some purities still have no rate for <strong>{status.branch}</strong> today (
              {status.date}). Invoices and the in-store TV board both read these rates.
            </>
          ) : (
            <>
              No rate has been set for <strong>{status.branch}</strong> today ({status.date}
              ). Invoices and the in-store TV board both read these rates, so it is the
              first job of the day.
            </>
          )}
        </p>
        {status.missing?.length > 0 && (
          <p className="mt-2 text-sm">
            Still to set: <strong>{status.missing.join(", ")}</strong>
          </p>
        )}
        <div className="mt-6 flex flex-wrap gap-2">
          <Button
            className="rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
            onClick={() => {
              dismiss();
              navigate("/gold-rates");
            }}
            data-testid="rate-prompt-set-button"
          >
            Set today's rate
          </Button>
          <Button
            variant="outline"
            className="rounded-none"
            onClick={dismiss}
            data-testid="rate-prompt-later-button"
          >
            Later today
          </Button>
        </div>
      </div>
    </div>
  );
};
